import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Building2, Plus, Power, Copy, Users, LogOut, LayoutGrid, ShieldCheck, CalendarPlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Logo } from "@/components/brand/Logo";

const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";

const billBadge = (s) => {
  const map = { active: "bg-green-100 text-green-700", trial: "bg-blue-100 text-blue-700", past_due: "bg-amber-100 text-amber-700", canceled: "bg-red-100 text-red-600" };
  return <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${map[s] || "bg-slate-100 text-slate-500"}`}>{(s || "—").replace("_", " ")}</span>;
};

export default function PlatformAdmin() {
  const qc = useQueryClient();
  const { user, logout } = useAuth();
  const [create, setCreate] = useState(false);
  const { data: stats } = useQuery({ queryKey: ["platform-stats"], queryFn: () => api.get("/platform/stats").then((r) => r.data) });
  const { data: tenants } = useQuery({ queryKey: ["tenants"], queryFn: () => api.get("/platform/tenants").then((r) => r.data.tenants) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["tenants"] }); qc.invalidateQueries({ queryKey: ["platform-stats"] }); };

  const toggle = async (t) => {
    const next = t.status === "active" ? "suspended" : "active";
    if (!window.confirm(`${next === "suspended" ? "Suspend" : "Reactivate"} "${t.name}"? ${next === "suspended" ? "All its users will be locked out." : ""}`)) return;
    try { await api.put(`/platform/tenants/${t.id}/status`, { status: next }); toast.success(`${t.name} ${next}`); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const extendTrial = async (t) => {
    if (!window.confirm(`Extend "${t.name}"'s trial by 14 days?`)) return;
    try { await api.put(`/platform/tenants/${t.id}/subscription`, { extendTrialDays: 14 }); toast.success("Trial extended 14 days"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3"><Logo variant="wordmark-light" /><span className="text-xs font-mono uppercase tracking-wider text-tech flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Platform Console</span></div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-300">{user?.email}</span>
            <button onClick={logout} className="text-slate-300 hover:text-white flex items-center gap-1.5 text-sm" data-testid="platform-logout"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-navy">Client Workspaces</h1>
            <p className="text-sm text-slate-500">Provision and manage the businesses running on TDW Nexus AI</p>
          </div>
          <Button className="bg-electric hover:bg-tech gap-1.5" onClick={() => setCreate(true)} data-testid="new-tenant-btn"><Plus className="h-4 w-4" /> New Workspace</Button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={LayoutGrid} label="Workspaces" value={stats?.tenants} sub={`${stats?.activeTenants ?? 0} active`} />
          <StatCard icon={Users} label="Total Users" value={stats?.totalUsers} />
          <StatCard icon={Building2} label="Suspended" value={(stats?.tenants ?? 0) - (stats?.activeTenants ?? 0)} />
        </div>

        <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono"><tr><th className="text-left p-3">Workspace</th><th className="text-left p-3">Owner</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Billing</th><th className="text-left p-3">Users</th><th className="text-left p-3">Status</th><th className="p-3"></th></tr></thead>
            <tbody>
              {(tenants || []).map((t) => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tenant-row-${t.id}`}>
                  <td className="p-3 font-medium text-navy">{t.name}{t.id === "tdw" && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Primary</span>}</td>
                  <td className="p-3 text-slate-600">{t.ownerEmail ? (t.ownerPending ? <span className="text-slate-400 italic">{t.ownerEmail} · pending</span> : t.ownerEmail) : <span className="text-slate-400 italic">invite pending</span>}</td>
                  <td className="p-3 text-slate-600 capitalize">{t.plan || "—"}</td>
                  <td className="p-3">{billBadge(t.billingStatus)}</td>
                  <td className="p-3 text-slate-600">{t.counts?.users ?? 0}</td>
                  <td className="p-3">{t.status === "active" ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Active</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">Suspended</span>}</td>
                  <td className="p-3 text-right">
                    {t.id !== "tdw" && (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => extendTrial(t)} title="Extend trial 14 days" className="p-1.5 rounded hover:bg-blue-50 text-blue-500" data-testid={`extend-trial-${t.id}`}><CalendarPlus className="h-4 w-4" /></button>
                        <button onClick={() => toggle(t)} title={t.status === "active" ? "Suspend" : "Reactivate"} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`toggle-tenant-${t.id}`}><Power className="h-4 w-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {create && <CreateTenantDialog onClose={() => setCreate(false)} onCreated={refresh} />}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <div className="flex items-center gap-2 text-slate-400 text-xs font-mono uppercase tracking-wider"><Icon className="h-4 w-4" /> {label}</div>
      <p className="mt-2 font-heading text-3xl font-bold text-navy">{value ?? "—"}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function CreateTenantDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", ownerEmail: "", ownerName: "" });
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.post("/platform/tenants", form); setLink(r.data.inviteLink); onCreated(); toast.success("Workspace created"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent><DialogHeader><DialogTitle>New Client Workspace</DialogTitle>
      <DialogDescription>Create an isolated workspace and invite its Owner to set up their account.</DialogDescription></DialogHeader>
      {link ? (
        <div className="space-y-3" data-testid="tenant-created">
          <p className="text-sm text-slate-600">Workspace created. Share this secure Owner setup link (also emailed):</p>
          <div className="flex gap-2">
            <input className={`${input} font-mono text-xs`} readOnly value={link} data-testid="tenant-invite-link" />
            <Button type="button" variant="outline" className="shrink-0" onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button>
          </div>
          <Button className="w-full bg-electric hover:bg-tech" onClick={onClose}>Done</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="tenant-form">
          <input className={input} placeholder="Business / workspace name" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="tenant-name" />
          <input className={input} type="email" placeholder="Owner email" required value={form.ownerEmail} onChange={(e) => set("ownerEmail", e.target.value)} data-testid="tenant-owner-email" />
          <input className={input} placeholder="Owner name (optional)" value={form.ownerName} onChange={(e) => set("ownerName", e.target.value)} data-testid="tenant-owner-name" />
          <Button type="submit" disabled={busy} className="w-full bg-electric hover:bg-tech" data-testid="tenant-submit">{busy ? "Creating…" : "Create & Invite Owner"}</Button>
        </form>
      )}
    </DialogContent></Dialog>
  );
}
